import React, { useEffect, useState } from "react";
import { Card, Image, Skeleton, Typography } from "antd";
import { ClockCircleOutlined, FormOutlined, PlayCircleOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import PlaceholderImage from "../../placeholder-video-cover.png";
import CardMenuButton from "../CardMenuButton/CardMenuButton";
import "./VideoThumbnailCard.less";

const { Text } = Typography;

const VideoThumbnailCard = ({
    video,
    index = 0,
    className = "",
    hoverable = false,
    showTopOverlay = false,
    searchTerm = "",
    showMenu = false,
    onManageQuiz
}) => {
    const [vimeoThumbnailUrl, setVimeoThumbnailUrl] = useState("");

    const fetchVimeoThumbnail = async (videoData) => {
        try {
            const response = await fetch(`https://vimeo.com/api/v2/video/${videoData.embedId}.json`);
            const json = await response.json();
            setVimeoThumbnailUrl(json?.[0]?.thumbnail_large || "");
        } catch (e) {
            setVimeoThumbnailUrl("");
        }
    };

    useEffect(() => {
        if (video?.videoType === "vimeo" && video?.embedId) {
            fetchVimeoThumbnail(video);
        }
    }, [video?.videoType, video?.embedId, video]);

    const getVideoThumbnail = (videoData) => {
        if (videoData?.videoType === "youtube") {
            return (
                <Image
                    src={`https://img.youtube.com/vi/${videoData.embedId}/mqdefault.jpg`}
                    fallback={PlaceholderImage}
                    preview={false}
                    style={{ aspectRatio: "16 / 9" }}
                />
            );
        }
        if (videoData?.videoType === "vimeo") {
            return (
                <Image
                    src={vimeoThumbnailUrl}
                    fallback={PlaceholderImage}
                    preview={false}
                    style={{ aspectRatio: "16 / 9" }}
                />
            );
        }
        return (
            <Image
                src={PlaceholderImage}
                fallback={PlaceholderImage}
                preview={false}
                style={{ aspectRatio: "16 / 9" }}
            />
        );
    };

    const sourceLabelMap = {
        youtube: "YouTube",
        vimeo: "Vimeo",
        smartslide: "SmartSlides"
    };

    const formatDuration = (duration) => {
        const totalSeconds = Number(duration);
        if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
            return "--:--";
        }
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);
        if (hours > 0) {
            return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
        }
        return `${minutes}:${String(seconds).padStart(2, "0")}`;
    };

    const sourceLabel = sourceLabelMap[video?.videoType] || "Video";
    const videoLength = formatDuration(video?.duration);

    return (
        <Card
            className={`video-thumbnail-item ${className}`.trim()}
            cover={
                <div className="video-thumbnail-item__cover">
                    {getVideoThumbnail(video)}
                    {showTopOverlay && (
                        <>
                            <div className={`video-thumbnail-item__top-tags ${showMenu ? "video-thumbnail-item__top-tags--with-menu" : ""}`}>
                                <span className={`video-thumbnail-item__tag video-thumbnail-item__tag--${video?.videoType || "default"}`}>
                                    {sourceLabel}
                                </span>
                            </div>
                            <div className="video-thumbnail-item__play">
                                <PlayCircleOutlined />
                            </div>
                        </>
                    )}
                    {showMenu ? (
                        <CardMenuButton
                            className="video-thumbnail-item__menu-button"
                            items={[
                                {
                                    label: "Manage Quiz",
                                    key: "manage-quiz",
                                    icon: <FormOutlined />,
                                    disabled: !onManageQuiz
                                }
                            ]}
                            onMenuClick={(key) => {
                                if (key === "manage-quiz" && onManageQuiz) {
                                    onManageQuiz(video);
                                }
                            }}
                        />
                    ) : null}
                </div>
            }
            hoverable={hoverable}
            style={{ animationDelay: `${60 * index}ms` }}>
            <div className="video-thumbnail-item__body">
                <div className="video-thumbnail-item__title">
                    <Highlighter
                        searchWords={searchTerm ? [searchTerm] : []}
                        autoEscape
                        textToHighlight={video?.title || "Untitled video"}
                    />
                </div>
                <div className="video-thumbnail-item__meta-row">
                    <Text type="secondary">
                        <ClockCircleOutlined /> {videoLength}
                    </Text>
                    <Text type="secondary">{video?.embedId || "N/A"}</Text>
                </div>
            </div>
        </Card>
    );
};

export const VideoThumbnailCardSkeleton = () => {
    return (
        <Card
            className="video-thumbnail-item"
            cover={
                <div className="video-thumbnail-item__skeleton-cover video-thumbnail-item__cover">
                    <div className="video-thumbnail-item__top-tags">
                        <Skeleton.Button
                            active
                            size="small"
                            shape="round"
                            className="video-thumbnail-item__skeleton-tag"
                        />
                    </div>
                    <Skeleton.Image
                        active
                        className="video-thumbnail-item__skeleton-image"
                    />
                </div>
            }>
            <div className="video-thumbnail-item__body">
                <Skeleton.Input
                    active
                    size="small"
                    className="video-thumbnail-item__skeleton-title"
                />
                <div className="video-thumbnail-item__meta-row">
                    <Skeleton.Input
                        active
                        size="small"
                        className="video-thumbnail-item__skeleton-meta-left"
                    />
                    <Skeleton.Input
                        active
                        size="small"
                        className="video-thumbnail-item__skeleton-meta-right"
                    />
                </div>
            </div>
        </Card>
    );
};

export default VideoThumbnailCard;
